import { useRouter } from 'next/router'
import { createRef, useRef, useState, useEffect, useCallback } from 'react'

import ValidatorWrapper, { ValidatorField } from '@coxy/react-validator'

import dynamic from 'next/dynamic'

import { ICurrency } from '../../../../store/fetchers/currencies'
import { _estimateExchange } from '../../../../store/fetchers/wallets'
import { useAppSelector } from '../../../../store/hooks'
import { getCurrencies } from '../../../../store/reducers/currencies'

import ButtonBlue from '../../../../components/ui/button/_color/_blue'
import InputCalculator from '../../../../components/ui/calculator/components/input-calculator'
import Notice from '../../../../components/ui/notice'
import validatorRules from '../../../../libs/validator-rules'
import { formatCurrency } from '../../../../utils/formatters'

import { LoginStatuses, selectedAuthStatus } from '../../../../store/reducers/users'

import styles from './styles.module.scss'

import WrapIcon from './icons/wrap.svg'
import { useTranslation } from 'utils/use-translation'

const DEFAULT_FROM = '0.5'

const LoginForm = dynamic(() => import('../../../../components/ui/header/components/popup-login-form'), { ssr: false })

const POPUP_TYPE_LOANS = 'loans'

export default function ExchangeCalculator({ fromCurrency, toCurrency, defaultFromAmount }) {
  const { t, replacePlaceholders } = useTranslation()
   
  const validatorRule = 'exchangeDepositAmountLanding'
  const showBalance = false
  const listFullWidth = false
  const validator = createRef<ValidatorWrapper>()
  const timer = useRef<any>() // cannot set NodeJS.Timeout
  const router = useRouter()

  const currencies = useAppSelector(getCurrencies)

  const [from, setFrom] = useState<ICurrency>(null)
  const [isLoadingFrom, setLoadingFrom] = useState(false)
  const [_amountFrom, _setAmountFrom] = useState(defaultFromAmount || DEFAULT_FROM)
  const amountFrom = useRef(_amountFrom)

  const [to, setTo] = useState<ICurrency>(null)
  const [isLoadingTo, setLoadingTo] = useState(true)
  const [amountTo, setAmountTo] = useState('')

  const [estimateError, setEstimateError] = useState('')

  const [estimatedRate, setEstimatedRate] = useState(0)

  const [areValid, setAreValid] = useState(false)
  const [isOpenLoginBlock, setIsOpenLoginBlock] = useState(false)

  const authStatus = useAppSelector(selectedAuthStatus)

  const handleCreateExchange = async () => {
    if (!from || !to) return
    const estimatedExchange = {
      fromCurrency: from.code,
      fromNetwork: from.network,
      toCurrency: to.code,
      toNetwork: to.network,
      fromAmount: String(_amountFrom),
    }

    if (authStatus === LoginStatuses.LOGIN) {
      await router.push({
        pathname: '/account/exchange',
        query: estimatedExchange,
      })
    } else {
      setIsOpenLoginBlock(true)
    }
  }

  const handleSetFrom = (from: ICurrency) => {
    setCurrency(from)
  }

  const setAmountFrom = (value: string) => {
    _setAmountFrom(value)
    amountFrom.current = value
  }

  const handleSetTo = (to: ICurrency) => {
    setTo(to)
  }

  const prepareEstimate = async () => {
    if (!from || !to) return
    if (+_amountFrom <= 0 || _amountFrom === '') {
      const estimatedExchange = await _estimateExchange({
        fromCurrency: from.code,
        fromNetwork: from.network,
        toCurrency: to.code,
        toNetwork: to.network,
        fromAmount: '1',
      })
      setAmountTo(estimatedExchange?.toAmount || 0)
      estimatedExchange && calculateEstimatedRate(estimatedExchange)
    } else {
      const estimatedExchange = await _estimateExchange({
        fromCurrency: from.code,
        fromNetwork: from.network,
        toCurrency: to.code,
        toNetwork: to.network,
        fromAmount: _amountFrom,
      })
      setAmountTo(estimatedExchange?.toAmount || 0)
      estimatedExchange && calculateEstimatedRate(estimatedExchange)
    }
  }

  useEffect(() => {
    if (to) {
      prepareEstimate()
    }
  }, [to])

  useEffect(() => {
    if (currencies.length) {
      setFrom(
        currencies.find(
          (item) =>
            item.code.toLowerCase() === fromCurrency.ticker.toLowerCase() &&
            item.network.toLowerCase() === fromCurrency.network.toLowerCase(),
        ),
      )
      setTo(
        currencies.find(
          (item) =>
            item.code.toLowerCase() === toCurrency.ticker.toLowerCase() &&
            item.network.toLowerCase() === toCurrency.network.toLowerCase(),
        ),
      )
    }
  }, [currencies])

  const handleSetAmountFrom = (amount: string) => {
    setAmountFrom(amount)
  }

  const calculateEstimatedRate = (estimate) => {
    const { fromAmount, toAmount } = estimate
    setEstimatedRate(fromAmount === 1 ? toAmount : toAmount / fromAmount)
  }

  const handleClosePopup = useCallback(() => {
    setIsOpenLoginBlock(false)
  }, [])

  const handleSetAmountTo = () => {
    clearTimeout(timer.current)
    setAmountTo(amountTo)
    prepareEstimate()
  }

  useEffect(() => {
    const timerId = setTimeout(() => {
      runCalculation()
    }, 350)

    return () => {
      clearTimeout(timerId)
    }
  }, [_amountFrom])

  const runCalculation = () => {
    from && prepareEstimate()
  }

  useEffect(() => {
    if (from) {
      handleSetAmountFrom(amountFrom.current)
    }

    if (to) {
      handleSetAmountTo()
    }
  }, [to, from])

  const wrapCoins = () => {
    const temp = from
    setFrom(to)
    setTo(temp)
  }

  useEffect(() => {
    setEstimateError('')
    setLoadingFrom(true)
    setLoadingTo(true)
    if (currencies.length) {
      currencies.filter((c) => c.is_allowed_for_wallet)
    }
    setLoadingFrom(false)
    setLoadingTo(false)
  }, [currencies])

  const setCurrency = (from: ICurrency) => {
    setAmountFrom(defaultFromAmount || DEFAULT_FROM)
    setFrom(from)
  }

  return (
    <div className={styles.wrapper}>
      <ValidatorWrapper ref={validator}>
        <ValidatorField value={amountFrom.current} rules={validatorRules[validatorRule](t)}>
          {({ isValid, message }) => {
            setAreValid(isValid)
            return (
              <InputCalculator
                isExchangePage={true}
                onEnter={handleCreateExchange}
                coin={from}
                isLoading={isLoadingFrom}
                onChangeAmount={handleSetAmountFrom}
                onChangeCoin={handleSetFrom}
                value={_amountFrom}
                label={t('you_send')}
                list={currencies
                  .filter((item) => item.is_allowed_for_wallet)
                  .sort((a, b) => {
                    if (a.exchange_priority === null) return 1
                    if (b.exchange_priority === null) return -1
                    return a.exchange_priority - b.exchange_priority
                  })}
                className={styles.changeCoinWrapper}
                labelClassName={styles.inputLabel}
                inputClassName={styles.inputInput}
                inputWrapperClassName={styles.inputWrapper}
                bigCurBlock
                showBalance={showBalance}
                listFullWidth={listFullWidth}
                isExchangeLayout={true}
              >
                {(estimateError ||
                  (!isValid && !isLoadingFrom && (amountTo !== '—' || amountFrom.current !== '—'))) && (
                  <div className={styles.inputError}>
                    {estimateError || message}{' '}
                    {message === 'Not enough balance for exchange.' && (
                      <span
                        className={styles.inputError__link}
                        onClick={() => {
                          router.push({
                            pathname: '/account/wallets',
                          })
                        }}
                      >
                        {replacePlaceholders(t('top_up_wallet'), { code: from?.code })}
                      </span>
                    )}
                  </div>
                )}
              </InputCalculator>
            )
          }}
        </ValidatorField>
        <button className={styles.wrapBtn} onClick={wrapCoins}>
          <img src={WrapIcon} alt="wrap icon" />
        </button>
        <ValidatorField value={amountTo}>
          {({ isValid, message }) => {
            return (
              <InputCalculator
                onEnter={handleCreateExchange}
                coin={to}
                isLoading={isLoadingTo}
                onChangeAmount={handleSetAmountTo}
                onChangeCoin={handleSetTo}
                value={+_amountFrom <= 0.0000001 || _amountFrom === '' ? '0' : amountTo}
                label={t('you_get')}
                list={currencies
                  .filter((item) => item.is_allowed_for_wallet)
                  .sort((a, b) => {
                    if (a.exchange_priority === null) return 1
                    if (b.exchange_priority === null) return -1
                    return a.exchange_priority - b.exchange_priority
                  })}
                isStableFirst={true}
                className={styles.changeCoinWrapper}
                labelClassName={styles.inputLabel}
                inputClassName={styles.inputInput}
                inputWrapperClassName={styles.inputWrapper}
                bigCurBlock
                showBalance={showBalance}
                listFullWidth={listFullWidth}
                isExchangePage={true}
                isExchangeLayout={true}
              >
                {!estimateError && !isValid && !isLoadingTo && (amountTo !== '—' || amountFrom.current !== '—') && (
                  <div className={styles.inputError}>{message}</div>
                )}
              </InputCalculator>
            )
          }}
        </ValidatorField>
        <div className={styles.info}>
          <div className={styles.infoItem}>
            <div className={styles.info_rightSide}>
              <div className={styles.infoItemTitle}>{t('no_hidden_fees')}</div>
              <Notice alt={t('fees_notice')} blue>{t('rate_includes_fees')}</Notice>
            </div>
            <div className={styles.infoItemValue}>—</div>
          </div>

          <div className={styles.verticalLine}></div>

          <div className={styles.infoItem}>
            <div className={styles.info_rightSide}>
              <div className={styles.infoItemTitle}>{t('estimated_rate')}</div>
              <Notice alt={t('estimated_rate_notice')} blue>{t('estimated_rate_notice_info')}</Notice>
            </div>
            <div className={styles.infoItemValue}>
              {1} {formatCurrency(from, true)} ~{' '}
              {estimatedRate && isFinite(estimatedRate) ? estimatedRate.toString().slice(0, 11) : ''}{' '}
              {formatCurrency(to, true)}
            </div>
          </div>
        </div>
        <ButtonBlue
          className={styles.button}
          onClick={() => {
            areValid && handleCreateExchange()
          }}
        >{t('exchange')}</ButtonBlue>
      </ValidatorWrapper>
      {isOpenLoginBlock && <LoginForm isWallet={false} onCloseForm={handleClosePopup} type={POPUP_TYPE_LOANS} />}
    </div>
  )
}
